import type {
    StepperStepsConstantType,
    StepperResolvedStep,
    StepperVisualStatus,
    StepperStepEntry
} from "./type";
import {
    formatNumber
} from "../../helpers/locale";

export const STEPPER_EMPTY_STEPS: Array<StepperStepEntry> = [];

export const STEPPER_STATUS_NAME_KEYS: Record<StepperVisualStatus, keyof NCoreUIKit.Translation | undefined> = {
    completed: "stepper-status-completed",
    disabled: "stepper-status-disabled",
    upcoming: "stepper-status-upcoming",
    skipped: "stepper-status-skipped",
    error: "stepper-status-error",
    current: undefined
};

export const getStepperStatus = ({
    activeIndex,
    index,
    step
}: {
    step: StepperStepEntry;
    activeIndex: number;
    index: number;
}): StepperVisualStatus => {
    if(step.status === "upcoming") {
        return index === activeIndex ? "current" : "upcoming";
    }

    if(step.status) {
        return step.status;
    }

    if(index < activeIndex) {
        return "completed";
    }

    return index === activeIndex ? "current" : "upcoming";
};

export const getStepperSteps = ({
    isPressEnabled,
    activeLocale,
    activeIndex,
    isLinear,
    localize,
    steps
}: StepperStepsConstantType): Array<StepperResolvedStep> => {
    const total = formatNumber({
        locale: activeLocale,
        value: steps.length
    });

    return steps.map((step, index) => {
        const status = getStepperStatus({
            activeIndex,
            index,
            step
        });

        const isCurrent = index === activeIndex;

        const isDisabled = status === "disabled";

        const isPressable = isPressEnabled && !isCurrent && !isDisabled && (!isLinear || status === "completed" || status === "error");

        const statusKey = STEPPER_STATUS_NAME_KEYS[status];

        const getDescription = () => {
            if(status === "skipped") {
                return localize("stepper-skipped");
            }

            if(step.description) {
                return step.description;
            }

            return step.isOptional ? localize("stepper-optional") : undefined;
        };

        const description = getDescription();

        const name = [
            localize("stepper-step-of", [
                formatNumber({
                    locale: activeLocale,
                    value: index + 1
                }),
                total
            ]),
            step.title,
            statusKey ? localize(statusKey) : undefined,
            status === "skipped" ? undefined : step.description
        ].filter(Boolean).join(", ");

        return {
            isDone: status === "completed" || status === "skipped",
            key: step.key ?? `${index}`,
            description,
            isPressable,
            isDisabled,
            isCurrent,
            status,
            index,
            name,
            step
        };
    });
};
